import {
  DBTagMentionNode,
  ExegesisSlateBlock,
  ExegesisSlateChild,
  ExegesisSlateDocument,
  ICreateLinkParams,
  IDeleteLinkParams,
  PrivateContentRow,
  TagMentionNode,
} from '@rhizome/pgtypes';
import { safeNanoid } from '@rhizome/safe-nanoid';
import { Node } from 'slate';
import { BLOCK_REFERENCE_MENTION } from './slateConstants';

// convert slate document to db rows
export const slateToDB = (
  slateDocument: ExegesisSlateDocument,
  parent_note: string
) => {
  const dbList: Pick<
    PrivateContentRow,
    'slate_content' | 'slate_parent' | 'slate_index' | 'tags' | 'block_id'
  >[] = [];
  for (let i = 0; i < slateDocument.length; i++) {
    const el = slateDocument[i];
    const { newNode, tags } = convertSlateNodeToDBRow(el);
    dbList.push({
      slate_content: newNode,
      slate_parent: parent_note,
      slate_index: i,
      tags,
      block_id: newNode.id,
    });
  }
  return dbList;
};

export const convertSlateNodeToDBRow = (
  slateNode: ExegesisSlateBlock<TagMentionNode>
): { newNode: ExegesisSlateBlock<DBTagMentionNode>; tags: string[] } => {
  const newNode: ExegesisSlateBlock<DBTagMentionNode> = {
    ...slateNode,
    children: [],
  };
  // map from tag to its index in the array
  const tags = [];
  const tagMap = new Map<string, number>();

  for (const child of slateNode.children) {
    if ('type' in child && child.type === 'mention') {
      let index = tagMap.get(child.value);
      if (index === undefined) {
        index = tagMap.size;
        tagMap.set(child.value, index);
        tags.push(child.value);
      }
      newNode.children.push({
        ...child,
        value: index,
      });
    } else {
      newNode.children.push(child);
    }
  }

  return {
    newNode,
    tags,
  };
};

// go from a db response to a slate document
export const DBToSlate = (
  content: PrivateContentRow[]
): ExegesisSlateBlock<TagMentionNode>[] => {
  if (content === undefined) {
    return [];
  }
  const newContents: ExegesisSlateBlock<TagMentionNode>[] = [];
  for (const entry of content) {
    const newEntryContents = convertDBRowToSlateNode(entry);
    newContents.push(newEntryContents);
  }
  return newContents;
};
export const convertDBRowToSlateNode = (
  entry: Pick<PrivateContentRow, 'slate_content' | 'tags'>
): ExegesisSlateBlock<TagMentionNode> => {
  const newEntryContents: ExegesisSlateBlock<TagMentionNode> = {
    ...entry.slate_content,
    children: [],
  };
  for (const child of entry.slate_content?.children || []) {
    if ('type' in child && child.type === 'mention') {
      const newval = entry.tags[child.value];
      newEntryContents.children.push({
        ...child,
        value: newval,
      });
    } else {
      newEntryContents.children.push(child);
    }
  }
  return newEntryContents;
};

type slateMapRow = {
  content: ExegesisSlateBlock;
  note_index: number;
};

type diffingPartial = Pick<PrivateContentRow, 'slate_index' | 'block_id'> & {
  slate_content: ExegesisSlateBlock;
};

export const diffSlateContents = (
  editorState: ExegesisSlateDocument,
  prevEditorMap: Map<string, slateMapRow>,
  prevLinkSet: Set<string>,
  author: string
): {
  inserted: diffingPartial[];
  updated: diffingPartial[];
  deleted: string[];
  newMap: Map<string, slateMapRow>;
  newLinks: ICreateLinkParams[];
  deletedLinks: IDeleteLinkParams[];
  linkSet: Set<string>;
} => {
  const { newEditorMap, linkSet } = createEditorMap(editorState);
  const inserted: diffingPartial[] = [];
  const updated: diffingPartial[] = [];
  const deleted: string[] = [];
  for (let i = 0; i < editorState.length; i++) {
    const block = editorState[i];
    const prevBlock = prevEditorMap.get(block.id);
    // if there is no previous block, push new info into insert array
    if (prevBlock === undefined) {
      inserted.push({
        slate_content: block,
        slate_index: i,
        block_id: block.id,
      });
    }
    // if there is a previous block, push the new info to the update array
    // it is updated if the content is different, or the note index is different.
    else if (
      JSON.stringify(prevBlock.content) !== JSON.stringify(block) ||
      prevBlock.note_index !== i
    ) {
      updated.push({
        slate_content: block,
        slate_index: i,
        block_id: block.id,
      });
    }
  }
  const deletedLinks: IDeleteLinkParams[] = [];
  const newLinks: ICreateLinkParams[] = [];
  // if its in the new and not in the old, it's been added
  for (const link of Array.from(linkSet)) {
    if (!prevLinkSet.has(link)) {
      newLinks.push({
        comment: '',
        ...stringLinkToLinkObject(link, author),
      });
    }
  }
  // if its in the old and not in the new, it's been deleted
  for (const link of Array.from(prevLinkSet)) {
    if (!linkSet.has(link)) {
      deletedLinks.push({ ...stringLinkToLinkObject(link, author) });
    }
  }

  for (const block_id of prevEditorMap.keys()) {
    const nextBlock = newEditorMap.get(block_id);
    if (nextBlock === undefined) {
      deleted.push(block_id);
    }
  }
  return {
    inserted,
    updated,
    deleted,
    newMap: newEditorMap,
    linkSet,
    deletedLinks,
    newLinks,
  };
};

// string is of format `${node.id}${BLOCK_REFERENCE_MENTION_DELIMITER}${child.block_id}`
// first element is the source, second element is the target
// explanation: child is the block reference object, node is the block itself
const stringLinkToLinkObject = (
  link: string,
  author: string
): IDeleteLinkParams => {
  return {
    // from
    private_content_1: link.split(BLOCK_REFERENCE_MENTION_DELIMITER)[0],
    private_content_2: link.split(BLOCK_REFERENCE_MENTION_DELIMITER)[1],
    owner: author,
  };
};

export const BLOCK_REFERENCE_MENTION_DELIMITER = '\n';
export const createEditorMap = (
  contents: ExegesisSlateDocument
): {
  newEditorMap: Map<string, slateMapRow>;
  linkSet: Set<string>;
} => {
  const editorMap = new Map<string, slateMapRow>();
  const linkSet = new Set<string>();
  for (let i = 0; i < contents.length; i++) {
    const node = contents[i];
    editorMap.set(node.id, {
      content: node,
      note_index: i,
    });
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i];
      if ('type' in child && child.type === BLOCK_REFERENCE_MENTION) {
        linkSet.add(
          `${node.id}${BLOCK_REFERENCE_MENTION_DELIMITER}${child.block_id}`
        );
      }
    }
  }
  return { newEditorMap: editorMap, linkSet };
};

export const emptyDoc = () => {
  return [
    {
      type: 'p',
      id: safeNanoid(),
      children: [
        {
          text: '',
        },
      ],
    },
  ];
};

export const getTextFromNode = (node: ExegesisSlateBlock) => {
  let textValue = '';
  const descendants = Node.descendants(node);
  for (const descendant of descendants) {
    const node = descendant[0] as ExegesisSlateChild;
    // TODO: include mentions!!!!!!!!!!!!!!!!!!!!
    let text = '';
    if ('text' in node) {
      text = node?.text;
    } else if ('value' in node && typeof node.value === 'string') {
      text = node.value;
    }
    //TODO some jank code to remove weird edge cases that crash postgres
    text = text?.replace(/[^a-zA-Z0-9]/g, ' ') || '';
    textValue += ` ${text}`;
  }
  return textValue;
};
